import { useMemo } from 'react';
import useGetVehicles from '../../../API/hooks/vehicles/useGetVehicles';
import useGetDrivers from '../../../API/hooks/drivers/useGetDrivers';
import useGetDevices from '../../../API/hooks/devices/useGetDevices';
import { deviceOnlineMarkerColor } from './markerColors';
import { generateVehicleStatusColor } from '../../../utils/commonUtils';

/**
 * Same query string the Vehicles/Drivers/Devices pages use for their full lists, so
 * react-query serves the map from that cache entry instead of issuing a second request.
 * Group filters are deliberately left out: the map only needs an id -> colour lookup and
 * extra entries are harmless, while a filtered query would be a separate cache key.
 */
const FULL_LIST_QUERY = '?limit=1000&skip=0';

export type MarkerColorById = Record<string, string>;

const VEHICLE_MAP_TYPES = ['vehicle', 'trip', 'video'];

/**
 * Marker tint per entity id, keyed the same way the locations endpoints key their items:
 * vehicle/trip/video maps by vehicle `_id`, driver maps by driver `_id`, device maps by
 * the hardware `deviceId`. Ids missing from the result fall back to DEFAULT_MARKER_COLOR.
 *
 * `enabled: false` returns an empty map (every marker then falls back to the default
 * grey) and skips the list request entirely.
 */
function useMarkerColors(fleetId: string, entityType?: string, enabled: boolean = true): MarkerColorById {
  const isVehicleMap = enabled && VEHICLE_MAP_TYPES.includes(entityType || '');
  const isDriverMap = enabled && entityType === 'driver';
  const isDeviceMap = enabled && entityType === 'device';

  const { data: vehiclesData } = useGetVehicles(fleetId, FULL_LIST_QUERY, !!fleetId && isVehicleMap);
  const { data: driversData } = useGetDrivers(fleetId, FULL_LIST_QUERY, !!fleetId && isDriverMap);
  const { data: devicesData } = useGetDevices(fleetId, FULL_LIST_QUERY, !!fleetId && isDeviceMap);

  return useMemo(() => {
    const colors: MarkerColorById = {};

    if (isVehicleMap) {
      vehiclesData?.results?.forEach((vehicle) => {
        colors[vehicle._id] = generateVehicleStatusColor(vehicle.status);
      });
    }

    if (isDriverMap) {
      // a driver has no status of its own — colour by the vehicle they're assigned to
      driversData?.results?.forEach((driver) => {
        colors[driver._id] = generateVehicleStatusColor(driver.vehicle?.status);
      });
    }

    if (isDeviceMap) {
      devicesData?.results?.forEach((device) => {
        if (device.deviceId) {
          colors[device.deviceId] = deviceOnlineMarkerColor(device.isOnline);
        }
      });
    }

    return colors;
  }, [
    isVehicleMap,
    isDriverMap,
    isDeviceMap,
    vehiclesData?.results,
    driversData?.results,
    devicesData?.results,
  ]);
}

export default useMarkerColors;
